import type { ReactElement } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import AuthButton from 'app/pages/Auth/components/AuthButton';
import AuthHeading from 'app/pages/Auth/components/AuthHeading';
import AuthLink from 'app/pages/Auth/components/AuthLink';
import AuthLoading from 'app/pages/Auth/components/AuthLoading';
import PasswordField from 'app/pages/Auth/components/PasswordField';

import { useValidateResetToken, type TokenValidity } from '../hooks/useValidateResetToken';
import { useSetPassword } from '../hooks/useSetPassword';
import { PASSWORD_REGEX, PASSWORD_REQUIREMENT } from '../password-policy';
import PasswordStrengthMeter from './PasswordStrengthMeter';

const schema = yup
  .object({
    password: yup
      .string()
      .matches(PASSWORD_REGEX, PASSWORD_REQUIREMENT)
      .required('Password is required'),
    confirmPassword: yup
      .string()
      .oneOf([yup.ref('password')], 'Passwords do not match')
      .required('Please confirm your password'),
  })
  .required();

type ResetPasswordFormValues = yup.InferType<typeof schema>;

type LinkState = 'valid' | 'expired' | 'used' | 'invalid';

const INVALID_MESSAGES: Record<Exclude<LinkState, 'valid'>, string> = {
  expired: 'This link has expired. Please request a new password reset.',
  used: 'This link has already been used. Please request a new password reset.',
  invalid: 'This link is invalid. Please request a new password reset.',
};

function ResetPasswordForm(): ReactElement {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const {
    data: validity,
    isError: validateTokenHasErrored,
    isInitialLoading,
  } = useValidateResetToken(token);
  const { mutate: setPassword, isPending: isSettingPassword } = useSetPassword(token ?? '');

  const isInvitation = searchParams.get('isInvitation') === 'true';
  const title = isInvitation ? 'Set Your Password' : 'Reset Password';

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({ resolver: yupResolver(schema) });

  const passwordValue = watch('password') ?? '';

  function onSubmit(data: ResetPasswordFormValues): void {
    if (!token || isSettingPassword) return;

    setPassword(
      { password: data.password, confirm_password: data.confirmPassword },
      {
        onSuccess: () => navigate('/login'),
      }
    );
  }

  if (token && isInitialLoading) {
    return <AuthLoading message="Checking your link…" />;
  }

  const linkState = getLinkState(validity, validateTokenHasErrored);

  if (linkState && linkState !== 'valid') {
    return (
      <>
        <AuthHeading title={title} description={INVALID_MESSAGES[linkState]} />
        <div className="vtwo:flex vtwo:flex-col vtwo:gap-4">
          <AuthButton type="button" onClick={() => navigate('/forgot-password')}>
            Request new link
          </AuthButton>
          <AuthLink to="/login">Back to login</AuthLink>
        </div>
      </>
    );
  }

  return (
    <>
      <AuthHeading title={title} />
      <form className="vtwo:flex vtwo:flex-col vtwo:gap-6" onSubmit={handleSubmit(onSubmit)}>
        <div className="vtwo:flex vtwo:flex-col vtwo:gap-4">
          <PasswordField
            label={isInvitation ? 'Password' : 'New password'}
            hint={PASSWORD_REQUIREMENT}
            error={errors.password?.message}
            {...register('password')}
          >
            <PasswordStrengthMeter password={passwordValue} />
          </PasswordField>
          <PasswordField
            label={isInvitation ? 'Confirm password' : 'Confirm new password'}
            error={errors.confirmPassword?.message}
            {...register('confirmPassword')}
          />
        </div>

        <AuthButton type="submit" isLoading={isSettingPassword}>
          Set password
        </AuthButton>
      </form>
    </>
  );
}

export default ResetPasswordForm;

function getLinkState(
  validity?: TokenValidity,
  validateTokenHasErrored?: boolean
): LinkState | null {
  if (validateTokenHasErrored) return 'invalid';

  if (!validity) return null;
  if (validity.used) return 'used';
  if (validity.expired) return 'expired';

  return 'valid';
}
